import React, { useState, useEffect } from 'react';
import { Sunrise, Sun, CloudSun, Sunset, Moon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WINDOW_ICONS = {
  morning: Sunrise,
  midday: Sun,
  afternoon: CloudSun,
  late_afternoon: Sunset,
  evening: Moon,
};

const ScheduleStep = ({ config, draft, updateDraft, nextStep, prevStep }) => {
  const [availability, setAvailability] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);

  // Fetch availability whenever date changes
  useEffect(() => {
    if (!draft.date) {
      setAvailability(null);
      return;
    }
    let cancelled = false;
    setLoadingAvail(true);
    fetch(`${BACKEND_URL}/api/availability?date=${draft.date}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        setAvailability(d.windows || null);
        // If the currently selected window is now full, clear it
        if (draft.time_window && d.windows?.[draft.time_window]?.available === false) {
          updateDraft({ time_window: '' });
        }
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoadingAvail(false));
    return () => {
      cancelled = true;
    };
  }, [draft.date]);

  const handleSubmit = (e) => {
    e.preventDefault();
    nextStep();
  };

  return (
    <form onSubmit={handleSubmit} className="form-step" data-testid="schedule-step">
      <h2 className="step-title">Pick Your Date & Time</h2>
      <p className="step-description">We require at least 1 day notice so we can assign your cleaning professional</p>

      <div className="form-group">
        <label className="form-label">Service Date</label>
        <input
          type="date"
          className="form-input"
          value={draft.date}
          min={config.min_booking_date}
          onChange={(e) => updateDraft({ date: e.target.value })}
          required
          data-testid="date-input"
        />
      </div>

      <div className="form-group">
        <label className="form-label">
          Arrival Window
          {loadingAvail && <span className="form-optional"> (checking availability…)</span>}
        </label>
        <div className="window-cards" data-testid="window-cards">
          {config.time_windows.map((w) => {
            const Icon = WINDOW_ICONS[w.key] || Sun;
            const slot = availability?.[w.key];
            const isFull = slot && !slot.available;
            const isLow = slot && slot.available && slot.remaining <= 1;
            return (
              <button
                type="button"
                key={w.key}
                className={`window-card ${draft.time_window === w.key ? 'selected' : ''} ${isFull ? 'full' : ''}`}
                onClick={() => !isFull && updateDraft({ time_window: w.key })}
                disabled={isFull}
                data-testid={`window-${w.key}`}
              >
                <Icon size={20} strokeWidth={1.5} />
                <span className="window-card-label">{w.label}</span>
                {isFull && <span className="window-card-status full">Fully booked</span>}
                {isLow && <span className="window-card-status low">{slot.remaining} left</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">How often?</label>
        <div className="frequency-pills" data-testid="frequency-pills">
          {config.frequencies.map((f) => (
            <button
              type="button"
              key={f.key}
              className={`frequency-pill ${draft.frequency === f.key ? 'selected' : ''}`}
              onClick={() => updateDraft({ frequency: f.key })}
              data-testid={`frequency-${f.key}`}
            >
              {f.label}
              {f.discount > 0 && <span className="frequency-save">Save {Math.round(f.discount * 100)}%</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Special Instructions <span className="form-optional">(optional)</span></label>
        <textarea
          className="form-input form-textarea"
          value={draft.notes}
          onChange={(e) => updateDraft({ notes: e.target.value })}
          placeholder="Anything we should know about this property..."
          rows={3}
          data-testid="notes-input"
        />
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={prevStep} data-testid="schedule-step-back">
          Back
        </button>
        <button type="submit" className="btn btn-primary" disabled={!draft.time_window} data-testid="schedule-step-next">
          Continue
        </button>
      </div>
    </form>
  );
};

export default ScheduleStep;
